import { injectable } from 'inversify';
import { Component } from '../../concepts/objects';
import { resolve } from '../Container';
import { Camera } from './Camera';
import { EditorSession } from './EditorSession';
import { PointerInfo, PointerTarget } from './PointerInfo';
import { RootOverride } from './RootOverride';
import { Selection } from './Selection';
import { AutoBringToFront } from './entities/AutoBringToFront';
import { CanSelectWhenLocked } from './entities/CanSelectWhenLocked';
import { InteractionType, Physical } from './entities/Physical';
import { OverlayPointerTarget } from './overlay/Overlay';
import { IsElevated } from './workflow/IsElevated';

@injectable()
export class InteractionHelper {

    @resolve(Camera) private camera!: Camera;
    @resolve(EditorSession) private session!: EditorSession;

    public getPointer() {
        return this.session.get(PointerInfo);
    }

    public getAll() {
        return this.session.get(PointerInfo).targets;
    }

    public getTop(): PointerTarget | null {
        return this.getAll()[0] ?? null;
    }

    public getTopAsOverlayTarget(): OverlayPointerTarget | null {
        const top = this.getTop();
        return (top instanceof Component) ? null : top;
    }

    public getTopAsComponent(): Physical | null {
        const { targets } = this.session.get(PointerInfo);
        const root = this.session.get(RootOverride);

        const t = targets[0] ?? null;
        if (t && t !== root) {
            if (t instanceof Component && t.is(Physical)) {
                return t;
            }
        }
        return null;
    }

    public getFirstComponent(): Physical | null {
        const { targets } = this.session.get(PointerInfo);
        const root = this.session.get(RootOverride);
        for (const t of targets) {
            if (t && t !== root) {
                if (t instanceof Component && t.is(Physical)) {
                    return t;
                }
            }
        }
        return null;
    }

    public testFor(type: InteractionType, force?: boolean): [Physical?, string?] {
        const target = this.getTopAsComponent();
        const elevated = this.session.get(IsElevated);
        const selected = this.session.get(Selection).has(target);
        // If no target, false result.
        if (!target) return [];
        // Except for invoke, ignore unforced interactions on locked and unselected elements
        if (type === 'select') {
            if (target.locked && !force && !(target.is(CanSelectWhenLocked) && elevated) && !target.is(AutoBringToFront)) {
                return [];
            }
        }
        // Can't mutate on locked elements
        else if (type === 'mutate') {
            if (target.locked) return [];
        }
        const mpt = this.camera.toModel(this.session.get(PointerInfo).position);
        const [hit, hint] = target.testInteraction({ type, mpt, selected });
        // If positive test, return result.
        return hit ? [target, hint] : [];
    }
}
